import { Component } from '@theme/component';
import { hydrateDeferredImages } from '@theme/deferred-image';
import { debounce, onDocumentReady, setHeaderMenuStyle } from '@theme/utilities';
import { MegaMenuHoverEvent, SectionHydratedEvent } from '@theme/events';

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  /**
   * Stable reference to the host header component.
   * Needed for disconnect cleanup when closest() is no longer available.
   * @type {HTMLElement | null}
   */
  #hostHeaderComponent = null;

  /**
   * Stable reference to the section that owns this menu.
   * @type {HTMLElement | null}
   */
  #hostSection = null;

  /**
   * @type {number | null}
   */
  #desktopImagePreparationRaf = null;

  /**
   * @type {MutationObserver | null}
   */
  #submenuMutationObserver = null;

  connectedCallback() {
    super.connectedCallback();
    this.#hostHeaderComponent = /** @type {HTMLElement | null} */ (this.closest('header-component'));
    this.#hostSection = /** @type {HTMLElement | null} */ (this.closest('.shopify-section'));

    onDocumentReady(this.#handleDocumentReady);
    window.addEventListener('resize', this.#resizeListener);
    this.#hostSection?.addEventListener(SectionHydratedEvent.eventName, this.#handleSectionHydrated);
    this.overflowMenu?.addEventListener('pointerleave', this.#overflowSubmenuListener);
    this.addEventListener('keydown', this.#handleKeyDown);
  }

  disconnectedCallback() {
    const headerComponent = this.#hostHeaderComponent;
    super.disconnectedCallback();
    window.removeEventListener('resize', this.#resizeListener);
    this.#hostSection?.removeEventListener(SectionHydratedEvent.eventName, this.#handleSectionHydrated);
    document.body.removeEventListener('pointermove', this.#onPointerMove);
    if (this.#state.activeItem) {
      this.#stopPointerTracking(this.#state.activeItem);
    }
    this.overflowMenu?.removeEventListener('pointerleave', this.#overflowSubmenuListener);
    this.removeEventListener('keydown', this.#handleKeyDown);
    this.#cleanupMutationObserver();

    if (this.#desktopImagePreparationRaf !== null) {
      cancelAnimationFrame(this.#desktopImagePreparationRaf);
      this.#desktopImagePreparationRaf = null;
    }

    this.#setHeaderOpenClass(false, headerComponent);
  }

  /**
   * Debounced resize event listener to recalculate menu style
   */
  #resizeListener = debounce(() => {
    setHeaderMenuStyle();
    this.#scheduleDesktopMenuImagePreparation();
  }, 100);

  #handleDocumentReady = () => {
    setHeaderMenuStyle();
    this.#scheduleDesktopMenuImagePreparation();
  };

  #handleSectionHydrated = () => {
    this.#scheduleDesktopMenuImagePreparation();
  };

  #overflowSubmenuListener = () => {
    this.#deactivate();
  };

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
  };

  #dismissedItem = null;

  #handleKeyDown = (event) => {
    if (event.key !== 'Escape' || !this.#state.activeItem) return;
    event.preventDefault();
    event.stopPropagation();
    const item = this.#state.activeItem;
    this.#dismissedItem = item;
    this.#deactivate(item, true);
    item.focus({ preventScroll: true });
  };

  /**
   * @type {ReturnType<typeof setTimeout> | undefined}
   */
  #pointerIdleTimer;

  /**
   * Last known pointer position for Safari hit-test reconciliation.
   * @type {{ x: number, y: number }}
   */
  #lastPointer = { x: 0, y: 0 };

  /**
   * Update the safety box idle state on the active menu item.
   * @param {PointerEvent} event
   */
  #onPointerMove = (event) => {
    const activeLink = this.#state.activeItem;
    if (!activeLink) return;

    this.#lastPointer.x = event.clientX;
    this.#lastPointer.y = event.clientY;

    const moving = Math.abs(event.movementX) >= 1 || event.movementY >= 1;
    activeLink.dataset.safetyBox = `${moving}`;

    clearTimeout(this.#pointerIdleTimer);
    if (moving) {
      this.#pointerIdleTimer = setTimeout(() => {
        if (this.#state.activeItem) {
          this.#state.activeItem.dataset.safetyBox = 'false';
          this.#reconcilePointerTarget();
        }
      }, 50);
    } else {
      this.#reconcilePointerTarget();
    }
  };

  /**
   * Check if the pointer is over a different menu item and trigger activation if so.
   * Works around Safari not re-evaluating hit targets after pseudo-element changes.
   */
  #reconcilePointerTarget() {
    const { x, y } = this.#lastPointer;
    requestAnimationFrame(() => {
      const target = document.elementFromPoint(x, y);
      if (!target) return;
      const listItem = target.closest('.menu-list__list-item');
      if (listItem && !listItem.contains(this.#state.activeItem)) {
        listItem.dispatchEvent(new PointerEvent('pointerenter', { bubbles: false }));
      }
    });
  }

  /**
   * Begin pointer tracking for the safety box on the newly active item.
   * @param {HTMLElement} item
   * @param {HTMLElement | null} previousItem
   */
  #startPointerTracking(item, previousItem) {
    if (previousItem) {
      this.#stopPointerTracking(previousItem);
    } else {
      document.body.addEventListener('pointermove', this.#onPointerMove);
    }

    const rect = item.getBoundingClientRect();
    const isOverlap = this.headerComponent?.hasAttribute('data-submenu-overlap-bottom-row');
    const boundary = isOverlap ? this.headerComponent?.querySelector('.header__row--top') : this.headerComponent;
    item.style.setProperty('--box-height', `${(boundary?.getBoundingClientRect().bottom ?? 0) - rect.top}px`);
  }

  /**
   * Stop pointer tracking and remove all safety box properties from an item.
   * @param {HTMLElement} item
   */
  #stopPointerTracking(item) {
    clearTimeout(this.#pointerIdleTimer);
    this.#pointerIdleTimer = undefined;
    item.style.removeProperty('--box-height');
    delete item.dataset.safetyBox;
  }

  /**
   * Get the overflow menu
   */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]'));
  }

  /**
   * Whether the overflow list is hovered
   * @returns {boolean}
   */
  get overflowListHovered() {
    return this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow-list"]')?.matches(':hover') ?? false;
  }

  get headerComponent() {
    return /** @type {HTMLElement | null} */ (this.closest('header-component')) ?? this.#hostHeaderComponent;
  }

  /**
   * Activate the selected menu item immediately
   * @param {PointerEvent | FocusEvent} event
   */
  activate = (event) => {
    this.dispatchEvent(new MegaMenuHoverEvent());

    if (!(event.target instanceof Element) || !this.headerComponent) return;

    let item = findMenuItem(event.target);

    if (item === this.#dismissedItem && event.type === 'focus') return;
    this.#dismissedItem = null;

    if (!item || item == this.#state.activeItem) return;

    const isDefaultSlot = event.target.slot === '';

    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    const previouslyActiveItem = this.#state.activeItem;

    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded = 'false';
    }

    this.#state.activeItem = item;
    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';

    // Header hydration can finish after the initial promo-render attempts.
    // Ensure the active menu has its promo content before measuring its height.
    renderNavigationPromosForHeaderMenu(this, true);

    let submenu = findSubmenu(item);
    const hasSubmenu = Boolean(submenu);

    if (!hasSubmenu && !isDefaultSlot) {
      submenu = this.overflowMenu;
    }

    hydrateDeferredImages(submenu || this).catch(console.error);

    if (submenu) {
      // Mark submenu as active for content-visibility optimization
      submenu.dataset.active = '';

      // Cleanup any existing mutation observer from previous menu activations
      this.#cleanupMutationObserver();

      // Monitor DOM mutations to catch submenu content injection from section
      // hydration AND attribute reversions from morph (which reverts hydrated
      // deferred images back to placeholder state).
      this.#submenuMutationObserver = new MutationObserver(() => {
        hydrateDeferredImages(submenu).catch(console.error);

        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            if (submenu.offsetHeight > 0) {
              this.#updateActiveSubmenuHeight();
            }
          });
        });
      });
      this.#submenuMutationObserver.observe(submenu, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['data-src', 'data-srcset', 'data-sizes'],
      });
    }

    let finalHeight = submenu?.offsetHeight || 0;

    // For overflow menu, the height needs to be either content of the submenu or the total height of the menu list links
    if (!isDefaultSlot) {
      const overflowListHeight = this.#getOverflowListLinksHeight();
      if (hasSubmenu) {
        /* Note: When the submenu is inside the overflow menu, its offsetHeight is not valid due to the lack of padding
         * we could add the padding variables to the submenu.offsetHeight, but measuring the overflowMenu.offsetHeight is just easier */
        finalHeight = this.#getOverflowSubmenuHeight(overflowListHeight);
      } else {
        finalHeight = overflowListHeight;
      }
    }

    if (!submenu) {
      // If there is no content to open, don't try to open it
      finalHeight = 0;
    }

    this.headerComponent.style.setProperty('--submenu-height', `${finalHeight}px`);
    this.#setFullOpenHeaderHeight(finalHeight);
    this.style.setProperty('--submenu-opacity', '1');
    this.#setHeaderOpenClass(finalHeight > 0);
    this.#startPointerTracking(item, previouslyActiveItem);
  };

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    if (!(event.target instanceof Element)) return;
    if (event.type === 'blur' && event.relatedTarget !== this.#dismissedItem) this.#dismissedItem = null;

    const menu = findSubmenu(this.#state.activeItem);
    const isMovingWithinMenu = event.relatedTarget instanceof Node && menu?.contains(document.activeElement);
    const isMovingToSubmenu =
      event.relatedTarget instanceof Node && event.type === 'blur' && menu?.contains(event.relatedTarget);
    const isMovingToOverflowMenu =
      event.relatedTarget instanceof Node && event.relatedTarget.parentElement?.matches('[slot="overflow"]');

    if (isMovingWithinMenu || isMovingToOverflowMenu || isMovingToSubmenu) {
      if (this.#state.activeItem) {
        this.#stopPointerTracking(this.#state.activeItem);
      }
      return;
    }

    this.#deactivate();
  }

  /**
   * Deactivate the active item immediately
   * @param {HTMLElement | null} [item]
   */
  #deactivate = (item = this.#state.activeItem, force = false) => {
    if (!item || item != this.#state.activeItem) return;

    // Don't deactivate if the overflow menu or overflow list is still being hovered
    if (!force && (this.overflowListHovered || this.overflowMenu?.matches(':hover'))) return;

    this.headerComponent?.style.setProperty('--submenu-height', '0px');
    this.#setFullOpenHeaderHeight(0);
    this.style.setProperty('--submenu-opacity', '0');
    this.#setHeaderOpenClass(false);
    this.dataset.overflowExpanded = 'false';

    const submenu = findSubmenu(item);

    document.body.removeEventListener('pointermove', this.#onPointerMove);
    this.#stopPointerTracking(item);
    this.#cleanupMutationObserver();

    this.#state.activeItem = null;
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';

    // Remove active state from submenu after animation completes
    if (submenu) {
      delete submenu.dataset.active;
    }
  };

  #getOverflowListLinksHeight() {
    const slottedMenuLinks = this.overflowMenu?.querySelector('slot')?.assignedElements();
    if (!slottedMenuLinks) return this.overflowMenu?.offsetHeight || 0;

    /**
     * @param {(submenu: HTMLElement) => void} cb
     */
    const mapSubmenus = (cb) => {
      slottedMenuLinks.forEach((link) => {
        const submenu = /** @type {HTMLElement | null} */ (link.querySelector('[ref="submenu[]"]'));
        if (submenu) {
          cb(submenu);
        }
      });
    };

    mapSubmenus((submenu) => {
      submenu.style.setProperty('display', 'none');
    });
    const height = this.overflowMenu?.offsetHeight || 0;
    mapSubmenus((submenu) => {
      submenu.style.removeProperty('display');
    });
    return height;
  }

  /**
   * Measure an overflow submenu without relying on its offsetHeight, which excludes overflow menu padding.
   * @param {number} [overflowListHeight]
   */
  #getOverflowSubmenuHeight(overflowListHeight = this.#getOverflowListLinksHeight()) {
    const overflowHeight = this.overflowMenu?.offsetHeight || 0;
    return Math.max(overflowHeight, overflowListHeight);
  }

  /**
   * Calculate and set the full open header height. If the submenu is not open, the full open header height is 0.
   * @param {number} submenuHeight
   */
  #setFullOpenHeaderHeight(submenuHeight) {
    if (!this.headerComponent) return;

    const isOverlapSituation = this.headerComponent.hasAttribute('data-submenu-overlap-bottom-row');

    const headerVisibleHeight =
      isOverlapSituation && this.headerComponent.offsetHeight > 0
        ? /** @type {HTMLElement | null} */ (this.headerComponent.querySelector('.header__row--top'))?.offsetHeight ?? 0
        : this.headerComponent.offsetHeight;

    const nothingToOpen = submenuHeight === 0;
    const fullOpenHeaderHeight = nothingToOpen ? 0 : submenuHeight + (headerVisibleHeight ?? 0);

    this.headerComponent?.style.setProperty('--full-open-header-height', `${fullOpenHeaderHeight}px`);
  }

  /**
   * Toggle header-open class on header elements.
   * @param {boolean} isOpen
   * @param {HTMLElement | null} [headerComponent]
   */
  #setHeaderOpenClass(isOpen, headerComponent = this.headerComponent) {
    if (!(headerComponent instanceof HTMLElement)) return;

    headerComponent.classList.toggle('header-open', isOpen);

    const header = headerComponent.querySelector('.header');
    if (header instanceof HTMLElement) {
      header.classList.toggle('header-open', isOpen);
    }
  }

  /**
   * Recalculate the active submenu height after late promo insertion or hydration.
   */
  #updateActiveSubmenuHeight() {
    const submenu = findSubmenu(this.#state.activeItem);
    if (!submenu || !this.headerComponent) return;

    const isOverflowItem = this.#state.activeItem?.parentElement?.slot === 'overflow';
    const submenuHeight = isOverflowItem ? this.#getOverflowSubmenuHeight() : submenu.offsetHeight;
    if (submenuHeight <= 0) return;

    this.headerComponent.style.setProperty('--submenu-height', `${submenuHeight}px`);
    this.#setFullOpenHeaderHeight(submenuHeight);
    this.#setHeaderOpenClass(true);
  }

  /**
   * Prepares desktop mega-menu images after menu style has been resolved.
   *
   * Deferred images use a placeholder src so mobile browsers never download
   * desktop mega-menu imagery. On desktop we hydrate them immediately on page
   * load so they're ready when the user opens a menu.
   */
  #scheduleDesktopMenuImagePreparation = () => {
    if (this.#desktopImagePreparationRaf !== null) {
      cancelAnimationFrame(this.#desktopImagePreparationRaf);
    }

    this.#desktopImagePreparationRaf = requestAnimationFrame(() => {
      this.#desktopImagePreparationRaf = null;
      renderNavigationPromosForHeaderMenu(this);
      this.#preloadImages();
      this.#hydrateDeferredImagesForDesktop().catch(console.error);
      this.#updateActiveSubmenuHeight();
    });
  };

  /**
   * Preload images that are set to load lazily.
   */
  #preloadImages = () => {
    if (!this.#isDesktopMenuMode()) return;

    const images = this.querySelectorAll('img[loading="lazy"]:not([data-deferred-image="manual"])');
    images?.forEach((image) => image.removeAttribute('loading'));
  };

  /**
   * Hydrates deferred images when the header is using desktop menu mode.
   * No-ops on mobile so hidden mega-menu images stay unloaded.
   * @param {ParentNode | null | undefined} [root]
   */
  #hydrateDeferredImagesForDesktop = async (root = this) => {
    if (!this.#isDesktopMenuMode()) return;
    await hydrateDeferredImages(root);
  };

  /**
   * @returns {boolean}
   */
  #isDesktopMenuMode() {
    return this.headerComponent?.dataset.menuStyle === 'menu';
  }

  #cleanupMutationObserver() {
    this.#submenuMutationObserver?.disconnect();
    this.#submenuMutationObserver = null;
  }
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * @param {HTMLElement} headerMenu
 * @param {boolean} [onlyMissing]
 */
function renderNavigationPromosForHeaderMenu(headerMenu, onlyMissing = false) {
  const headerComponent = headerMenu.closest('header-component');
  if (!headerComponent) return;

  const promoGroupSelector = '[data-navigation-promo-group][data-navigation-promo-placement^="top_level_"]';
  const promoGroups = [...headerComponent.querySelectorAll(promoGroupSelector)];

  headerMenu.querySelectorAll('[data-navigation-promo-top-level-index]').forEach((grid) => {
    if (!(grid instanceof HTMLElement)) return;

    const existingPromoItems = grid.querySelectorAll(
      '.mega-menu__navigation-promo-list .menu-drawer__featured-content-list-item--manual'
    );
    if (onlyMissing && existingPromoItems.length > 0) return;

    const topLevelIndex = grid.dataset.navigationPromoTopLevelIndex;
    const promoItems = promoGroups
      .filter(
        (group) => group instanceof HTMLElement && group.dataset.navigationPromoTopLevelIndex === topLevelIndex
      )
      .flatMap((group) => [...group.querySelectorAll('.menu-drawer__featured-content-list-item--manual')])
      .filter((item) => item.querySelector('.menu-drawer__featured-card-image'))
      .slice(0, 4);

    if (promoItems.length === 0) {
      removeNavigationPromos(grid);
      return;
    }

    applyNavigationPromos(grid, promoItems);
  });
}

/**
 * @param {HTMLElement} grid
 * @param {Element[]} promoItems
 */
function applyNavigationPromos(grid, promoItems) {
  const menuList = grid.querySelector('.mega-menu__list');
  if (!(menuList instanceof HTMLElement)) return;

  const promoContent = getNavigationPromoContent(grid, menuList);
  const promoList = promoContent.querySelector('.mega-menu__navigation-promo-list');
  if (!(promoList instanceof HTMLElement)) return;

  promoList.replaceChildren(...promoItems.map((item) => cloneNavigationPromoItem(item)));
  promoList.style.setProperty('--navigation-promo-item-size', promoItems.length <= 2 ? '300px' : '150px');

  grid.querySelectorAll('.mega-menu__content:not(.mega-menu__content--navigation-promos)').forEach((content) => {
    if (!(content instanceof HTMLElement)) return;

    content.hidden = true;
    content.dataset.navigationPromoHidden = 'true';
  });

  setNavigationPromoColumns(grid, menuList, promoContent);
}

/**
 * @param {HTMLElement} grid
 * @param {HTMLElement} menuList
 * @returns {HTMLElement}
 */
function getNavigationPromoContent(grid, menuList) {
  let promoContent = grid.querySelector('.mega-menu__content--navigation-promos');

  if (!(promoContent instanceof HTMLElement)) {
    promoContent = document.createElement('span');
    promoContent.className = 'mega-menu__content mega-menu__content--navigation-promos';
    promoContent.innerHTML = '<ul class="mega-menu__navigation-promo-list list-unstyled" role="list"></ul>';
    grid.append(promoContent);
  }

  const imageBorderRadius = menuList.style.getPropertyValue('--menu-image-border-radius');
  if (imageBorderRadius) {
    promoContent.style.setProperty('--resource-card-corner-radius', imageBorderRadius);
  }

  return promoContent;
}

/**
 * @param {HTMLElement} grid
 * @param {HTMLElement} menuList
 * @param {HTMLElement} promoContent
 */
function setNavigationPromoColumns(grid, menuList, promoContent) {
  const styles = getComputedStyle(grid);
  const desktopColumns = Number.parseInt(styles.getPropertyValue('--menu-columns-desktop'), 10) || 6;
  const tabletColumns = Number.parseInt(styles.getPropertyValue('--menu-columns-tablet'), 10) || 4;
  const promoColumnsDesktop = Math.min(2, Math.max(1, desktopColumns - 1));
  const promoColumnsTablet = Math.min(2, Math.max(1, tabletColumns - 1));

  menuList.style.setProperty('--menu-columns-desktop', `${Math.max(1, desktopColumns - promoColumnsDesktop)}`);
  menuList.style.setProperty('--menu-columns-tablet', `${Math.max(1, tabletColumns - promoColumnsTablet)}`);
  promoContent.style.setProperty('--menu-content-columns-desktop', `${promoColumnsDesktop}`);
  promoContent.style.setProperty('--menu-content-columns-tablet', `${promoColumnsTablet}`);
}

/**
 * @param {Element} promoItem
 * @returns {Node}
 */
function cloneNavigationPromoItem(promoItem) {
  const clone = promoItem.cloneNode(true);

  if (clone instanceof HTMLElement) {
    [clone, ...clone.querySelectorAll('*')].forEach((element) => {
      [...element.attributes].forEach((attribute) => {
        if (attribute.name.startsWith('data-shopify')) {
          element.removeAttribute(attribute.name);
        }
      });
    });
  }

  return clone;
}

/**
 * @param {HTMLElement} grid
 */
function removeNavigationPromos(grid) {
  grid.querySelector('.mega-menu__content--navigation-promos')?.remove();

  const menuList = grid.querySelector('.mega-menu__list');
  if (menuList instanceof HTMLElement) {
    menuList.style.removeProperty('--menu-columns-desktop');
    menuList.style.removeProperty('--menu-columns-tablet');
  }

  grid.querySelectorAll('[data-navigation-promo-hidden]').forEach((content) => {
    if (!(content instanceof HTMLElement)) return;

    content.hidden = false;
    delete content.dataset.navigationPromoHidden;
  });
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  if (element?.matches('[slot="more"')) {
    // Select the first overflowing menu item when hovering over the "More" item
    return findMenuItem(element.parentElement?.querySelector('[slot="overflow"]'));
  }

  return element?.querySelector('[ref="menuitem"]');
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}
